// O lembrete no cache do react-query (passo 18, PR 2). Puro, sem React.
//
// ⚠️ As chaves têm cópia PRÓPRIA aqui (como `useChatEdits.ts` tem a dele): 10 testes da `ChatPage` trocam
// `@/hooks/useChat` por uma factory explícita que não exporta `MESSAGES_KEY` nem `isChatRoomsListKey`. O teste
// `cache.test.ts` amarra `ehListaDeSalas` a `isChatRoomsListKey`.
import type { ChatMessage, Lembrete, LembreteDoPainel, PainelDeLembretes } from "@/types/chat";

export const chaveDasMensagens = (roomId: string) => ["chat", "rooms", roomId, "messages"];
/** O painel "⏰ Lembretes" (`GET /chat/notas/lembretes`). */
export const CHAVE_DO_PAINEL = ["chat", "notas", "lembretes"];

/** A LISTA de salas (nunca uma query escopada numa sala) — a mesma regra de `isChatRoomsListKey`. */
export function ehListaDeSalas(query: { queryKey: readonly unknown[] }): boolean {
  const k = query.queryKey;
  return (
    k[0] === "chat" &&
    k[1] === "rooms" &&
    (k.length === 2 || (k.length === 3 && (k[2] == null || typeof k[2] === "object")))
  );
}

/** Cancelado é "sem lembrete": a bolha não mostra nada. */
export function lembreteVisivel(l: Lembrete | null | undefined): Lembrete | null {
  return l && l.status !== "cancelado" ? l : null;
}

/** O cache com o lembrete da nota `messageId` trocado, em qualquer página. Sem cache, `undefined`. */
export function comLembreteNaMensagem(
  emCache: ChatMessage[] | undefined,
  messageId: string,
  lembrete: Lembrete | null
): ChatMessage[] | undefined {
  return emCache?.map((m) =>
    m?.id === messageId ? { ...m, lembrete: lembreteVisivel(lembrete) } : m
  );
}

const porHora = (a: LembreteDoPainel, b: LembreteDoPainel) =>
  Date.parse(a.lembrar_em) - Date.parse(b.lembrar_em);

/**
 * O painel com o lembrete `lembreteId` no lugar do novo estado (`null` = cancelado: some). O trecho vem do item
 * que já estava no painel. Usado para a linha mudar NA HORA; o refetch confirma.
 */
export function painelDepoisDe(
  painel: PainelDeLembretes | undefined,
  lembreteId: string,
  novo: Lembrete | null
): PainelDeLembretes | undefined {
  if (!painel) return painel;
  const todos = [...painel.tocaram, ...painel.proximos, ...painel.concluidos];
  const antigo = todos.find((i) => i.id === lembreteId);
  const sem = (lista: LembreteDoPainel[]) => lista.filter((i) => i.id !== lembreteId);
  const base: PainelDeLembretes = {
    tocaram: sem(painel.tocaram),
    proximos: sem(painel.proximos),
    concluidos: sem(painel.concluidos),
    concluidos_total:
      painel.concluidos_total - (painel.concluidos.some((i) => i.id === lembreteId) ? 1 : 0),
  };
  const visivel = lembreteVisivel(novo);
  if (!visivel || !antigo) return base;
  const item: LembreteDoPainel = { ...visivel, trecho: antigo.trecho };
  if (visivel.status === "tocou") base.tocaram = [...base.tocaram, item];
  else if (visivel.status === "pendente") base.proximos = [...base.proximos, item].sort(porHora);
  else {
    base.concluidos = [item, ...base.concluidos];
    base.concluidos_total += 1;
  }
  return base;
}

/**
 * "Concluídos" do painel: a 1ª página (viva, rebuscada a cada ação) seguida da cauda do "Mostrar mais", SEM repetir
 * lembrete. A cauda só deve existir enquanto a 1ª página é a mesma em que ela foi pedida (o painel a zera quando a
 * página é rebuscada); aqui a deduplicação por `id` é a segunda trava: uma chave repetida vira linha dupla na tela.
 */
export function juntarConcluidos(
  primeira: LembreteDoPainel[],
  cauda: LembreteDoPainel[]
): LembreteDoPainel[] {
  const vistos = new Set(primeira.map((i) => i.id));
  const novos: LembreteDoPainel[] = [];
  for (const item of cauda) {
    if (vistos.has(item.id)) continue;
    vistos.add(item.id);
    novos.push(item);
  }
  return [...primeira, ...novos];
}
